import React from 'react';
import numeral from 'numeral';
import { Cell } from 'react-table';
import { CreateUserType, ICustomer, ISummary, IOrder, IUser } from '../types';
import { KitchenRole } from '../types/kitchen.types';
import moment from 'moment';
import { FulfilmentTimer } from './orders.constants';
import { updatedAtColumn } from './table.constants';

// Helper function to get user type display text
const getUserTypeDisplay = (user: IUser): string => {
  // If user has admin roles, show admin role
  if (user.admin && user.admin.roles && user.admin.roles.length > 0) {
    return user.admin.roles[0];
  }

  // If user has kitchen staff data, show mapped kitchen role
  if (user.kitchenStaff && user.kitchenStaff.length > 0) {
    const kitchenStaff = user.kitchenStaff[0];
    const primaryRole =
      kitchenStaff.role && kitchenStaff.role.length > 0
        ? kitchenStaff.role[0]
        : null;

    if (primaryRole) {
      switch (primaryRole as KitchenRole) {
        case 'OWNER':
          return 'Outlet Owner';
        case 'SERVER':
          return 'Outlet Officer';
        case 'DELIGHT_OFFICER':
          return 'Officer';
        default:
          return primaryRole;
      }
    }
  }

  // Default to Customer
  return 'Customer';
};

// Map a kitchen role value to its display label used across the codebase
export const mapKitchenRoleToLabel = (
  role?: KitchenRole | string | null
): string => {
  if (!role) return '';
  switch (role as KitchenRole) {
    case 'OWNER':
      return 'Outlet Owner';
    case 'SERVER':
      return 'Outlet Officer';
    case 'DELIGHT_OFFICER':
      return 'Officer';
    default:
      return role;
  }
};

const customerColumns = [
  {
    Header: 'Name',
    Cell: ({ row }: any) =>
      row.original.fullName ||
      `${row.original.firstName || ''} ${row.original.lastName || ''}`.trim(),
  },

  {
    Header: 'Phone Number',
    accessor: 'phoneNumber',
  },
  {
    Header: 'Email',
    accessor: 'email',
  },
  updatedAtColumn,
  {
    Header: 'Action',
  },
];

// Guests have no per-row action; the row itself is clickable to open the
// guest detail page, so this column set omits the Action column.
const guestColumns = [
  {
    Header: 'Name',
    Cell: ({ row }: any) =>
      row.original.fullName ||
      `${row.original.firstName || ''} ${row.original.lastName || ''}`.trim(),
  },
  {
    Header: 'Phone Number',
    accessor: 'phoneNumber',
  },
  {
    Header: 'Email',
    accessor: 'email',
  },
  updatedAtColumn,
];

const customerOrderColumns = [
  {
    Header: 'Order Number',
    accessor: 'slug',
  },
  {
    Header: 'Date & Time',
    accessor: 'createdAt',
  },
  {
    Header: 'Fulfilment time',
    accessor: 'timePreparing',
    Cell: ({ row }: Cell) => <FulfilmentTimer order={row.original as IOrder} />,
  },
  {
    Header: 'Method of Payment',
    accessor: 'methodOfPayment',
  },
  {
    Header: 'Total Price',
    accessor: 'totalPrice',
    Cell: ({ row }: Cell) => `₦${numeral(row.values.totalPrice).format('0,0')}`,
  },
  {
    Header: 'Status',
    accessor: 'status',
  },
];

const gender = [
  { value: 'MALE', option: 'Male' },
  { value: 'FEMALE', option: 'Female' },
  { value: 'OTHER', option: 'Other' },
] as { value: string; option: string }[];

const AdminRoles = [
  { value: 'OWNER', option: 'Owner' },
  { value: 'OPERATIONS', option: 'Operations' },
  { value: 'CUSTOMER_SUCCESS', option: 'Customer Success' },
  { value: 'LOGISTICS', option: 'Logistics' },
  { value: 'MARKETING', option: 'Marketing' },
  { value: 'FINANCE', option: 'Finance' },
  { value: 'INITIATOR', option: 'Initiator' },
  { value: 'PHARMACIST', option: 'Pharmacist' },
] as { value: string; option: string }[];

const addressLabels = [
  { value: 'OFFICE', option: 'Office' },
  { value: 'HOME', option: 'Home' },
  { value: 'OTHER', option: 'Other' },
  { value: 'WORK', option: 'Work' },
];

export const pilotStatus = [
  { value: 'REJECTED', option: 'Rejected' },
  { value: 'VERIFIED', option: 'Verified' },
  { value: 'PENDING', option: 'Pending' },
] as { value: string; option: string }[];

export const vendorKitchenColumn = {
  Header: 'Kitchen',
  id: 'kitchen',
  Cell: ({ row }: any) => {
    const user = row.original as IUser;
    if (user.kitchenStaff && user.kitchenStaff.length > 0) {
      return user.kitchenStaff.map((ks) => ks.kitchen.name).join(', ');
    }
    return '—';
  },
};

export const usersColumns = [
  {
    id: 'select',
    Header: (table: any) => {
      return (
        <>
          <input
            type="checkbox"
            {...table.getToggleAllRowsSelectedProps()}
            indeterminate="false"
          />
        </>
      );
    },
    Cell: ({ row }: any) => (
      <input
        type="checkbox"
        {...row.getToggleRowSelectedProps()}
        indeterminate="false"
        onClick={(e) => e.stopPropagation()}
      />
    ),
  },
  {
    Header: 'Name',
    Cell: ({ row }: any) =>
      row.original.firstName + ' ' + row.original.lastName,
  },
  {
    Header: 'Username',
    accessor: 'username',
  },

  {
    Header: 'User type',
    accessor: 'admin',
    Cell: ({ row }: any) => getUserTypeDisplay(row.original),
  },

  {
    Header: 'Phone Number',
    accessor: 'phoneNumber',
  },
  {
    Header: 'Email',
    accessor: 'email',
  },
  updatedAtColumn,
  {
    Header: 'Action',
  },
];

export const userInfoCards = (
  isCustomer: boolean,
  customer: { customer: ICustomer; summary: ISummary } | null,
  user: { user: IUser; summary: ISummary } | null
) => [
  {
    title: 'Total Order Value',
    value: `₦${
      numeral(
        isCustomer
          ? customer?.summary.totalOrderValue
          : user?.summary.totalOrderValue
      ).format('0,0') !== 'NaN'
        ? numeral(
            isCustomer
              ? customer?.summary.totalOrderValue
              : user?.summary.totalOrderValue
          ).format('0,0')
        : '0'
    }`,
    bg: 'orange-gradient',
  },
  {
    title: 'Number of orders',
    value: isCustomer
      ? customer?.summary.numberOfOrders
      : user?.summary.numberOfOrders,
    bg: 'blue-gradient',
  },
  {
    title: 'Date of last order',
    value:
      moment(
        isCustomer
          ? customer?.summary.dateOfLastOrder
          : user?.summary.dateOfLastOrder
      )
        .format('L')
        .toLowerCase() !== 'invalid date'
        ? moment(
            isCustomer
              ? customer?.summary.dateOfLastOrder
              : user?.summary.dateOfLastOrder
          ).format('L')
        : '-',
    bg: 'purple-gradient',
  },
  {
    title: 'Wallet Balance',
    value: `₦${
      numeral(
        isCustomer
          ? customer?.summary.walletBalance
          : user?.summary.walletBalance
      ).format('0,0') !== 'NaN'
        ? numeral(
            isCustomer
              ? customer?.summary.walletBalance
              : user?.summary.walletBalance
          ).format('0,0')
        : '0'
    }`,
    bg: 'red-gradient',
  },
];

export const userDetails = (
  isCustomer: boolean,
  customer?: { customer: ICustomer; summary: ISummary } | null,
  user?: { user: IUser; summary: ISummary } | null
) => [
  {
    title: 'Customer Name',
    content: isCustomer
      ? customer?.customer.fullName
      : user?.user.firstName + ' ' + user?.user.lastName,
  },
  {
    title: 'Email Address',
    content: isCustomer ? customer?.customer.email : user?.user.email,
  },
  {
    title: 'Phone Number',
    content: isCustomer
      ? customer?.customer.phoneNumber
      : user?.user.phoneNumber,
  },
  {
    title: 'Username',
    content: user?.user.username,
    user: true,
  },
  {
    title: 'Address',
    content: user?.user.defaultAddress?.address,
    user: true,
  },
];

export {
  customerColumns,
  guestColumns,
  gender,
  AdminRoles,
  customerOrderColumns,
  addressLabels,
};

export const userTypes: { name: string; value: CreateUserType }[] = [
  { name: 'User', value: 'USER' },
  { name: 'Kitchen staff', value: 'KITCHEN_STAFF' },
];
